import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import {
  asyncScheduler,
  BehaviorSubject,
  SchedulerLike,
  Subscription,
  timer,
} from 'rxjs';

export interface NotificationMessage {
  readonly severity?:
    'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast';
  readonly summary?: string;
  readonly detail?: string;
  readonly sticky?: boolean;
  readonly closable?: boolean;
  readonly life?: number;
  readonly key?: string;
  readonly id?: string | number;
}

export interface VisibleNotification extends NotificationMessage {
  readonly notificationId: number;
}

export const NOTIFICATION_SCHEDULER = new InjectionToken<SchedulerLike>(
  'Notification expiry scheduler',
  { providedIn: 'root', factory: () => asyncScheduler },
);

/** Application notices are independent; a new message never dismisses an older one. */
@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  private readonly state = new BehaviorSubject<readonly VisibleNotification[]>(
    [],
  );
  readonly messages$ = this.state.asObservable();
  private readonly expirations = new Map<number, Subscription>();
  private nextId = 0;

  constructor(
    @Inject(NOTIFICATION_SCHEDULER) private readonly scheduler: SchedulerLike,
  ) {}

  add(message: NotificationMessage): void {
    const notification: VisibleNotification = {
      ...message,
      notificationId: ++this.nextId,
    };
    this.state.next([...this.state.value, notification]);
    if (!message.sticky) {
      const life =
        message.life !== undefined && Number.isFinite(message.life)
          ? Math.max(0, message.life)
          : 3000;
      const subscription = timer(life, this.scheduler).subscribe(() =>
        this.dismiss(notification.notificationId),
      );
      if (!subscription.closed)
        this.expirations.set(notification.notificationId, subscription);
    }
  }

  addAll(messages: readonly NotificationMessage[]): void {
    messages.forEach((message) => this.add(message));
  }

  dismiss(notificationId: number): void {
    this.expirations.get(notificationId)?.unsubscribe();
    this.expirations.delete(notificationId);
    this.state.next(
      this.state.value.filter(
        (message) => message.notificationId !== notificationId,
      ),
    );
  }

  clear(key?: string): void {
    for (const message of this.state.value) {
      if (key === undefined || message.key === key)
        this.dismiss(message.notificationId);
    }
  }

  ngOnDestroy(): void {
    this.expirations.forEach((subscription) => subscription.unsubscribe());
    this.expirations.clear();
    this.state.complete();
  }
}
